import React, { useState } from 'react';
import type { Aktivitet } from '@klp/formgenerator/interfaces';
import { Block } from '@klp/design/Block';
import type { Theme } from '@material-ui/core';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    IconButton,
    Paper,
    TextField,
    Typography as MuiTypography,
} from '@material-ui/core';
import { ActionButton } from '@klp/design/Action';
import { ExpandMore } from '@material-ui/icons';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { useTranslation } from 'react-i18next';
import { Typography } from '@klp/design/Typography';
import { CamundaFields } from '@forvalter/utils/CamundaUtils';
import { useRoleMapping } from '@klp/leverandor/hooks';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { useFormAuth } from '@klp/formgenerator/hooks';
import { useDispatch } from 'react-redux';
import { leverandorActions } from '../../store/leverandorSlice';
import {
    formatDateWithTime,
    parseSafeDate,
} from '@klp/utils/dateTimeFormatter';
import {
    useRolleKommentar,
    type RolleKommentar,
} from './hooks/useRolleKommentar';
import { useReplyToRolleKommentar } from './hooks/useReplyToRolleKommentar';

const styles = (theme: Theme) => ({
    comment: {
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    },
    accordionTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
    },
    replyContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        marginTop: '8px',
    },
    replyActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px',
    },
    metadataBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: theme.palette.secondary.light,
        padding: theme.spacing(0.75, 1.5),
        borderRadius: '4px 4px 0 0',
        marginBottom: theme.spacing(0.5),
    },
    metadataLabel: {
        color: theme.palette.secondary.dark,
        fontSize: '0.8125rem',
    },
    metadataValue: {
        color: theme.palette.primary.main,
        fontWeight: 500,
        fontSize: '0.8125rem',
    },
    metadataSeparator: {
        color: theme.palette.secondary.main,
        margin: theme.spacing(0, 0.75),
    },
    commentCard: {
        borderRadius: '4px',
        marginBottom: theme.spacing(1),
    },
    commentBody: {
        padding: theme.spacing(1, 1.5),
    },
    commentFooter: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: theme.spacing(0, 1, 0.5, 1),
    },
    replyCard: {
        borderLeft: `3px solid ${theme.palette.tertiary?.main}`,
        borderRadius: '0 4px 4px 0',
        marginLeft: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    deepReplyCard: {
        borderLeft: `3px solid ${theme.palette.tertiary?.main}`,
        borderRadius: '0 4px 4px 0',
        marginLeft: theme.spacing(4),
        marginBottom: theme.spacing(1),
    },
    replyMetadataBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: theme.palette.tertiary?.veryLight,
        padding: theme.spacing(0.75, 1.5),
        borderRadius: '0 4px 0 0',
        marginBottom: theme.spacing(0.5),
    },
});

export type RolleKommentarListProps = {
    currentActivity: Aktivitet;
    prosessInstansId: string;
    aktiviteter?: Array<Aktivitet>;
};

export const RolleKommentarList: React.FC<RolleKommentarListProps> = ({
    currentActivity,
    prosessInstansId,
    aktiviteter,
}) => {
    const classes = useKlpStyles({}, styles);
    const { t, i18n } = useTranslation();
    const { handleMapping: handleRoleMapping } = useRoleMapping();
    const dispatch = useDispatch();
    const { allComments, isRolleKommentarerFieldInTheForm } =
        useRolleKommentar(currentActivity);
    const { isActivityEditable } = useFormAuth();
    const { replyToComment, isLoading } = useReplyToRolleKommentar({
        activity: currentActivity,
        prosessInstansId,
        culture: i18n.language,
    });

    const isEditable = isActivityEditable(currentActivity);

    const uniqueRoles = [
        ...new Set(
            (aktiviteter ?? [])
                .filter((a) => a.startDato !== null)
                .map((a) => a.kandidatGruppe),
        ),
    ];

    const handleAddComment = (e: React.MouseEvent) => {
        e.stopPropagation();
        dispatch(
            leverandorActions.setActiveModal({
                type: 'sendKommentar',
                prosessInstansId,
                roller: uniqueRoles,
                defaultRolle: currentActivity.kandidatGruppe,
                activity: currentActivity,
            }),
        );
    };

    const [accordionExpanded, setAccordionExpanded] = useState(
        isActivityEditable(currentActivity),
    );
    const [replyingToIndex, setReplyingToIndex] = useState<number | null>(null);
    const [replyText, setReplyText] = useState('');

    if (!isRolleKommentarerFieldInTheForm) return null;

    const handleAccordionChange = () => {
        setAccordionExpanded(!accordionExpanded);
    };

    const handleReplyClick = (index: number) => {
        setReplyingToIndex(index);
        setReplyText('');
    };

    const handleCancelReply = () => {
        setReplyingToIndex(null);
        setReplyText('');
    };

    const handleSendReply = async (
        rolle: string,
        index: number,
        depth: number,
    ) => {
        if (!replyText.trim()) return;
        await replyToComment(rolle, replyText, index, depth);
        setReplyingToIndex(null);
        setReplyText('');
    };

    const numberOfComments = allComments.length;

    const MAX_REPLY_DEPTH = 2;

    const findParentUser = (index: number): string | undefined => {
        for (let i = index - 1; i >= 0; i--) {
            if (allComments[i].depth < allComments[index].depth) {
                return allComments[i].user;
            }
        }
        return undefined;
    };

    const renderMetadataBar = (
        comment: RolleKommentar,
        barClassName: string,
        targetLabel: string,
        targetValue: string,
    ) => (
        <Box className={barClassName}>
            <MuiTypography variant="body2" component="span">
                <MuiTypography
                    variant="caption"
                    component="span"
                    className={classes.metadataLabel}
                >
                    {t(
                        `leverandor.${CamundaFields.ROLLE_KOMMENTARER}.list.fra`,
                    )}
                </MuiTypography>{' '}
                <MuiTypography
                    variant="body2"
                    component="span"
                    className={classes.metadataValue}
                >
                    {comment.user}
                </MuiTypography>
                {targetValue && (
                    <>
                        <MuiTypography
                            variant="body2"
                            component="span"
                            className={classes.metadataSeparator}
                        >
                            ·
                        </MuiTypography>
                        <MuiTypography
                            variant="caption"
                            component="span"
                            className={classes.metadataLabel}
                        >
                            {targetLabel}
                        </MuiTypography>{' '}
                        <MuiTypography
                            variant="body2"
                            component="span"
                            className={classes.metadataValue}
                        >
                            {targetValue}
                        </MuiTypography>
                    </>
                )}
            </MuiTypography>
        </Box>
    );

    const renderCommentBody = (comment: RolleKommentar) => (
        <Box className={classes.commentBody}>
            <Typography variant="caption">
                {formatDateWithTime(parseSafeDate(comment.timestamp))}
            </Typography>
            <Typography variant="body2" className={classes.comment}>
                {comment.comment}
            </Typography>
        </Box>
    );

    return (
        <Accordion
            data-testid="LeverandorRolleKommentarerAccordion"
            expanded={accordionExpanded}
            onChange={handleAccordionChange}
        >
            <AccordionSummary expandIcon={<ExpandMore />}>
                <div className={classes.accordionTitle}>
                    {t(
                        `leverandor.${CamundaFields.ROLLE_KOMMENTARER}.list.title`,
                    )}
                    {isEditable && (
                        <IconButton size="small" onClick={handleAddComment}>
                            <Illustration
                                icon={IconName.CHAT}
                                klpPalette="secondary"
                                size="xsmall"
                            />
                        </IconButton>
                    )}
                </div>
            </AccordionSummary>
            <AccordionDetails>
                <Block>
                    {numberOfComments > 0 &&
                        allComments.map((comment, index) => (
                            <React.Fragment
                                key={`${comment.activityId}-${comment.timestamp}-${index}`}
                            >
                                {(() => {
                                    const isReply = comment.depth > 0;
                                    const replyDepth = Math.min(
                                        comment.depth + 1,
                                        MAX_REPLY_DEPTH,
                                    );
                                    const canReply =
                                        isActivityEditable(currentActivity) &&
                                        comment.depth < MAX_REPLY_DEPTH;
                                    const parentUser = isReply
                                        ? findParentUser(index)
                                        : undefined;
                                    const cardClass = isReply
                                        ? comment.depth >= 2
                                            ? classes.deepReplyCard
                                            : classes.replyCard
                                        : classes.commentCard;
                                    const barClass = isReply
                                        ? classes.replyMetadataBar
                                        : classes.metadataBar;
                                    const targetLabel = isReply
                                        ? t(
                                              `leverandor.${CamundaFields.ROLLE_KOMMENTARER}.list.svarTil`,
                                          )
                                        : t(
                                              `leverandor.${CamundaFields.ROLLE_KOMMENTARER}.list.til`,
                                          );
                                    const targetValue = isReply
                                        ? (parentUser ?? '')
                                        : (handleRoleMapping(comment.rolle) ??
                                          '');

                                    return (
                                        <Paper
                                            variant="outlined"
                                            className={cardClass}
                                        >
                                            {renderMetadataBar(
                                                comment,
                                                barClass,
                                                targetLabel,
                                                targetValue,
                                            )}
                                            {renderCommentBody(comment)}
                                            {canReply &&
                                                replyingToIndex !== index && (
                                                    <Box
                                                        className={
                                                            classes.commentFooter
                                                        }
                                                    >
                                                        <Button
                                                            size="small"
                                                            color="primary"
                                                            onClick={() =>
                                                                handleReplyClick(
                                                                    index,
                                                                )
                                                            }
                                                        >
                                                            {t(
                                                                `leverandor.${CamundaFields.ROLLE_KOMMENTARER}.list.reply`,
                                                            )}
                                                        </Button>
                                                    </Box>
                                                )}
                                            {replyingToIndex === index && (
                                                <Box
                                                    className={
                                                        classes.commentBody
                                                    }
                                                >
                                                    <Box
                                                        className={
                                                            classes.replyContainer
                                                        }
                                                    >
                                                        <TextField
                                                            fullWidth={true}
                                                            multiline={true}
                                                            variant="outlined"
                                                            size="small"
                                                            label={`${t(`leverandor.${CamundaFields.ROLLE_KOMMENTARER}.list.svarTil`)} ${comment.user}`}
                                                            value={replyText}
                                                            onChange={(e) =>
                                                                setReplyText(
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            autoFocus={true}
                                                        />
                                                        <Box
                                                            className={
                                                                classes.replyActions
                                                            }
                                                        >
                                                            <ActionButton
                                                                actionType="cancel"
                                                                onClick={
                                                                    handleCancelReply
                                                                }
                                                                disabled={
                                                                    isLoading
                                                                }
                                                                translation="general.cancel"
                                                            />
                                                            <ActionButton
                                                                actionType="submit"
                                                                onClick={() =>
                                                                    handleSendReply(
                                                                        comment.rolle,
                                                                        index,
                                                                        replyDepth,
                                                                    )
                                                                }
                                                                isLoading={
                                                                    isLoading
                                                                }
                                                                disabled={
                                                                    !replyText.trim()
                                                                }
                                                                translation={`leverandor.${CamundaFields.ROLLE_KOMMENTARER}.list.reply`}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Paper>
                                    );
                                })()}
                            </React.Fragment>
                        ))}
                    {numberOfComments === 0 && (
                        <Typography variant="body2">
                            {t(
                                `leverandor.${CamundaFields.ROLLE_KOMMENTARER}.list.empty`,
                            )}
                        </Typography>
                    )}
                </Block>
            </AccordionDetails>
        </Accordion>
    );
};
